import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from 'formik';
import useAxios from "./useAxios";
import Constants from "../Constants";
import LoggedInUserService from '../services/AuthService';
import { toaster } from '../utils/toast';
import * as Yup from 'yup';
import { getPresignedUrl, buildHeadshotFile } from '../utils/uploadUtils';
import uploadManager from '../services/UploadManager';
import { UPLOAD_PURPOSE } from '@demandproai/shared';
import { getPreviewUrlForFile } from '../utils/imagePreview';

const useHeadshot = () => {
    const { postData, getData } = useAxios();
    let navigate = useNavigate();
    const userData = LoggedInUserService.GetLoggedInUserData();
    const [isLoading, setIsLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [headshotData, setHeadshotData] = useState(null);

    useEffect(() => {
        getHeadshotData();
    }, []);

    const headshotValidationSchema = Yup.object().shape({
        headshot: Yup.mixed().nullable(),
        blurbText: Yup.string(),
    });

    const formik = useFormik({
        initialValues: {
            headshot: headshotData?.headshot || null,
            blurbText: headshotData?.blurbText || ""
        },
        enableReinitialize: true,
        validationSchema: headshotValidationSchema,
        onSubmit: (values) => {
            submitHeadshot(values);
        }
    });

    const submitHeadshot = async (values) => {
        setIsLoading(true);
        try {
            const payload = {
                ...values,
                headshot: (values.headshot?.fileName || values.headshot?.name)
                    ? {
                        s3UrlPath: values.headshot.uniqueKey || values.headshot.s3UrlPath,
                        name: values.headshot.fileName || values.headshot.name
                    }
                    : null
            }
            const res = await postData(Constants.ApiUrl.user.uploadHeadshot, payload);
            if (res?.success) {
                await getHeadshotData();
                toaster({ message: 'Headshot and introduction saved successfully', success: true });
                return res;
            } else {
                toaster({ message: res?.message || 'Failed to save headshot', success: false });
                return res;
            }
        } catch {
            toaster({ message: 'Error uploading headshot. Please try again.', success: false });
        } finally {
            setIsLoading(false);
        }
    };

    const handleFileSelection = useCallback(async ({ file: files }) => {
        setIsUploading(true);
        try {
            const preparedFiles = files.map((f) => ({
                file: f,
                name: f.name,
                metaData: {},
            }));
            const presignedData = await Promise.all(
                preparedFiles.map((fileData) =>
                    getPresignedUrl({
                        file: fileData.file,
                        fileName: fileData.name || fileData.file.name,
                        caseId: userData.id,
                        metaData: fileData.metaData || {},
                        purpose: UPLOAD_PURPOSE.HEADSHOT
                    })
                )
            );
            const headshotFiles = buildHeadshotFile(preparedFiles, presignedData);
            await uploadManager.uploadFile(headshotFiles[0]);
            const headshotFile = {
                s3UrlPath: headshotFiles[0].uniqueKey,
                name: headshotFiles[0].fileName,
                url: await getPreviewUrlForFile(headshotFiles[0].file)
            }
            formik.setFieldValue("headshot", headshotFile);
            setIsUploading(false);
            return { presignedData };
        } catch (error) {
            setIsUploading(false);
            toaster({ message: 'Failed to get the pre signed url', success: false });
            throw error;
        }
    }, []);

    const getHeadshotData = async () => {
        try {
            const res = await getData(Constants.ApiUrl.user.headshotData);
            if (res?.success) {
                setHeadshotData(res.data);
                return res.data;
            } else {
                toaster({ message: res?.message || 'Failed to fetch headshot data', success: false });
                return res;
            }
        } catch (err) {
            console.error('Error fetching headshot data:', err);
            return null;
        }
    };

    return {
        formik,
        isLoading,
        headshotValidationSchema,
        submitHeadshot,
        headshotData,
        navigate,
        isUploading,
        handleFileSelection
    };
};

export default useHeadshot;